import { Injectable, isDevMode } from '@angular/core';
import * as Sentry from '@sentry/angular';

const LOGS_STORAGE_KEY = 'innership:logs';

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private buildExtras(args: unknown[], excluded?: unknown): Record<string, unknown> {
    return args.reduce<Record<string, unknown>>((extras, arg, index) => {
      if (arg === excluded) {
        return extras;
      }

      extras[`arg${index}`] = arg;
      return extras;
    }, {});
  }

  private reportToSentry(level: 'error' | 'warning', message: string, args: unknown[]): void {
    const error = args.find((arg) => arg instanceof Error) as Error | undefined;
    const extras = this.buildExtras(args, error);

    Sentry.withScope((scope) => {
      scope.setLevel(level);
      scope.setTag('logger', 'LoggerService');
      scope.setContext('logger', {
        message,
        level,
      });

      Object.entries(extras).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });

      if (error) {
        Sentry.captureException(error);
        return;
      }

      Sentry.captureMessage(message, level);
    });
  }

  private shouldLog(): boolean {
    if (!isDevMode()) {
      return false;
    }

    if (typeof window === 'undefined') {
      return true;
    }

    try {
      return window.localStorage.getItem(LOGS_STORAGE_KEY) !== 'off';
    } catch {
      return true;
    }
  }

  enable(): void {
    if (!isDevMode() || typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(LOGS_STORAGE_KEY, 'on');
    } catch {
      // Ignore storage errors in restricted contexts.
    }
  }

  disable(): void {
    if (!isDevMode() || typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(LOGS_STORAGE_KEY, 'off');
    } catch {
      // Ignore storage errors in restricted contexts.
    }
  }

  error(message: string, ...args: unknown[]): void {
    this.reportToSentry('error', message, args);

    if (this.shouldLog()) {
      console.error(`[ERROR] ${message}`, ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog()) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  warnToSentry(message: string, ...args: unknown[]): void {
    this.reportToSentry('warning', message, args);
    this.warn(message, ...args);
  }

  log(message: string, ...args: unknown[]): void {
    if (this.shouldLog()) {
      console.log(`[LOG] ${message}`, ...args);
    }
  }
}
